import { FacebookCustomEvent } from '../../analytics/facebook';
import { FirebaseShareEvent } from '../../analytics/firebase';
import config from '../../config';
import { authInfo } from '../../ducks/auth.duck';
import applyConsent from '../consent/applyConsent';
import { getConsent } from '../consent/consentState';
import { checkNativeAppUpdate } from './nativeAppUpdate';
import { handleBackButton } from './backButton';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getClientData } from '../clientData';
import { logError } from '../log';
import { oneSignalInit } from './oneSignal';

// TODO Change to use MUI breakpoints
export const MODAL_BREAKPOINT = 900;

// TODO: isMobileSize replace with useMediaQuery
export const isMobileSize = () =>
  typeof window !== 'undefined' && window.innerWidth < MODAL_BREAKPOINT;

export const openDeviceShareDialog = (dialogOptions, listingId, currentUser) => {
  window.plugins.socialsharing.shareWithOptions(
    dialogOptions,
    (result) => {
      FirebaseShareEvent.withContentType('listing')
        .withItemId(listingId)
        .withMethod('deviceShareDialog')
        .logEvent();
      FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
        .withCustomName('deviceShareDialog')
        .withParams({ listingId })
        .logEvent();
      console.log('[socialsharing] shared to app', result);
    },
    (error) => {
      console.log('[socialsharing] sharing failed with message', JSON.stringify(error));
    },
  );
};

const EXCLUDED_PATHS = ['/api/newsletter/confirm'];

const initDeeplink = () => {
  IonicDeeplink.onDeepLink(function(data) {
    try {
      if (EXCLUDED_PATHS.some(excluded => data.path?.startsWith(excluded))) {
        console.log('[deeplink] excluded path, ignoring:', data.path);
        return;
      }

      console.log('[deeplink] received', JSON.stringify(data));

      let path = data.path;
      if (!path && data.url) {
        try {
          const parsed = new URL(data.url);
          path = parsed.pathname;
          console.log('[deeplink] parsed path from url:', path);
        } catch (urlError) {
          console.error('[deeplink] failed to parse url:', data.url, urlError);
        }
      }

      // Ensure path has a locale prefix — external links (e.g. Sharetribe password
      // reset emails) use bare paths like /reset-password which the RR7 data router
      // misinterprets as a locale param, landing the user on the homepage instead.
      if (path) {
        const segments = path.replace(/^\//, '').split('/');
        const supportedLocales = Object.keys(config.languages || {});
        if (segments[0] && !supportedLocales.includes(segments[0])) {
          const deviceLang = typeof navigator !== 'undefined' && navigator.language
            ? navigator.language.split('-')[0]
            : null;
          const locale = getClientData('vansite.locale')
            || (deviceLang && supportedLocales.includes(deviceLang) ? deviceLang : null)
            || config.fallbackLocale
            || 'de';
          path = `/${locale}${path}`;
          console.log('[deeplink] prepended locale:', path);
        }
      }

      const search = data.queryString
        ? (data.queryString.startsWith('?') ? data.queryString : '?' + data.queryString)
        : '';

      const deepLocation = {
        pathname: path || '/',
        search,
      };

      console.log('[deeplink] navigating to:', JSON.stringify(deepLocation));

      if (!window.routerNavigate) {
        let attempts = 0;
        const maxAttempts = 100;
        const wait = setInterval(() => {
          attempts++;
          if (window.routerNavigate) {
            clearInterval(wait);
            console.log('[deeplink] routerNavigate ready, navigating');
            window.routerNavigate(deepLocation);
          } else if (attempts >= maxAttempts) {
            clearInterval(wait);
            console.error('[deeplink] routerNavigate not available after 10s, giving up');
          }
        }, 100);
      } else {
        window.routerNavigate(deepLocation);
      }
    } catch (error) {
      console.error('[deeplink] error handling deep link:', error);
    }
  });
};

// index.js awaitet diese Promise VOR root.render() — sie darf deshalb unter
// KEINEN Umständen hängen bleiben, sonst mountet React nie (Whitescreen).
export const DEVICEREADY_WATCHDOG_MS = 10000;

export const initCordovaScripts = (dispatch) => {
  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve({});
    };

    // Feuert deviceready nie (Plugin-/Native-Fehler), rendern wir trotzdem —
    // App ohne Push/Deeplink ist besser als Whitescreen.
    const watchdog = setTimeout(() => {
      logError(
        new Error(`deviceready not fired within ${DEVICEREADY_WATCHDOG_MS}ms — rendering anyway`),
        'cordova-boot-watchdog',
      );
      settle();
    }, DEVICEREADY_WATCHDOG_MS);

    // Plugin-Init ist nicht boot-kritisch: Fehler loggen, nie den Mount blockieren.
    const safely = (label, fn) => {
      try {
        fn();
      } catch (e) {
        logError(e, 'cordova-boot-init-failed', { step: label });
      }
    };

    document.addEventListener(
      'deviceready',
      () => {
        clearTimeout(watchdog);
        settle();

        // status bar
        safely('statusbar', () => {
          window.StatusBar.overlaysWebView(false);
          window.StatusBar.styleLightContent();

          if (window.cordova.platformId === 'android') {
            window.StatusBar.backgroundColorByHexString('#000000');
          } else if (window.cordova.platformId === 'ios') {
            window.StatusBar.backgroundColorByName('black');
          }
        });

        // handle external links
        safely('inappbrowser', () => {
          window.open = window.cordova.InAppBrowser.open;
        });

        // init deeplink
        safely('deeplink', () => initDeeplink());

        // init one signal
        safely('onesignal', () => oneSignalInit());

        // native firebase analytics: the plugin namespace only exists from now
        // on — re-apply the stored consent decision so setConsent/setEnabled
        // reach the native SDK (boot-time initConsent ran before deviceready).
        safely('firebase-analytics', () => {
          const consent = getConsent();
          if (consent) applyConsent(consent);
        });

        safely('app-version', () => {
          window.cordova.getAppVersion().then(function(version) {
            window.currentAppVersion = version;
            checkNativeAppUpdate();
          });
        });
      },
      false,
    );

    document.addEventListener(
      'resume',
      async () => {
        checkNativeAppUpdate();

        // Re-validate auth token after app resumes from background.
        // iOS can suspend the app for hours, causing access tokens to expire.
        // The SDK's built-in refresh handles getting a new access token if the
        // refresh token is still valid. If it's not, authInfo will correctly
        // mark the user as unauthenticated.
        try {
          await dispatch(authInfo());
          const { isAuthenticated } = dispatch((_, getState) => getState().auth);
          if (isAuthenticated) {
            dispatch(fetchCurrentUser());
          }
        } catch (e) {
          logError(e, 'resume-auth-refresh-failed');
        }
      },
    );

    document.addEventListener('backbutton', handleBackButton, false);

    window.addEventListener(
      'load',
      function() {
        // open external links in system browser
        document.addEventListener('click', (e) => {
          if (e.target) {
            let link;
            if (e.target.nodeName === 'a') {
              link = e.target;
            } else {
              link = e.target.closest('a');
            }
            if (link && (link.target === '_blank' || link.target === '_system')) {
              e.preventDefault();
              window.open(link.href, '_system');
            }
          }
        });
      },
      false,
    );
  });
};

export const getTrackingPermission = async () => {
  if (!window?.cordova?.plugins?.idfa) return false;
  const idfaPlugin = window.cordova.plugins.idfa;
  let info = await idfaPlugin.getInfo();
  if (info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_AUTHORIZED) {
    return true;
  } else if (
    info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_DENIED ||
    info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_RESTRICTED
  ) {
    return false;
  } else if (info.trackingPermission === idfaPlugin.TRACKING_PERMISSION_NOT_DETERMINED) {
    const result = await idfaPlugin.requestPermission();
    return result === idfaPlugin.TRACKING_PERMISSION_AUTHORIZED;
  }
  return false;
};

export const openUrlInIOS = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
};